<template>
  <div
    :key="LKStore.project.id"
    class="charts-sections country-analytics-main"
  >
    <section class="rubrics-dynamic row-section">
      <div
        ref="refSentimentStatBar"
        class="main-tone half-panel"
      >
        <SentimentStatBar />
      </div>
      <div
        ref="refImportanceStatBar"
        class="rubrics-tone half-panel"
      >
        <ImportanceStatBar />
      </div>
    </section>

    <section class="rubrics-dynamic row-section">
      <div
        ref="refNewsDynamicChart"
        class="panel h-auto"
      >
        <NewsDynamicChart />
      </div>
    </section>

    <section class="rubrics-dynamic row-section">
      <div
        ref="refCategoryDynamicChart"
        class="panel h-auto"
      >
        <CategoryDynamicChart />
      </div>
    </section>

    <section class="news-tone row-section">
      <div
        ref="refDomainBlocksStatBar"
        class="rubrics-tone half-panel"
      >
        <DomainBlocksStatBar />
      </div>

      <div
        ref="refRegionStatBar"
        class="main-tone half-panel"
      >
        <RegionStatBar />
      </div>
    </section>
  </div>
</template>

<script setup>
import CategoryDynamicChart from "@/components/AnalyticsPage/AnalyticsGeneral/Charts/CategoryDynamicChart.vue";
import NewsDynamicChart from "@/components/AnalyticsPage/AnalyticsGeneral/Charts/NewsDynamicChart.vue";
import DomainBlocksStatBar from "@/components/AnalyticsPage/AnalyticsGeneral/Charts/DomainBlocksStatBar.vue";
import RegionStatBar from "@/components/AnalyticsPage/AnalyticsGeneral/Charts/RegionStatBar.vue";
import SentimentStatBar from "@/components/AnalyticsPage/AnalyticsGeneral/Charts/SentimentStatBar.vue";
import ImportanceStatBar from "@/components/AnalyticsPage/AnalyticsGeneral/Charts/ImportanceStatBar.vue";
import {useLK} from "@/controllerLK/LKstore";
import {ref, defineExpose} from "vue";

import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

// Импорт шрифтов как URL
import RobotoRegular from "@/assets/fonts/Roboto/Roboto-Regular.ttf";
import RobotoBold from "@/assets/fonts/Roboto/Roboto-Bold.ttf";

const LKStore = useLK();

// refs на все чарты
const refSentimentStatBar = ref(null);
const refImportanceStatBar = ref(null);
const refNewsDynamicChart = ref(null);
const refCategoryDynamicChart = ref(null);
const refDomainBlocksStatBar = ref(null);
const refRegionStatBar = ref(null);

// Массив чартов
const chartsArray = [refSentimentStatBar, refImportanceStatBar,
  refNewsDynamicChart, refCategoryDynamicChart, refDomainBlocksStatBar, refRegionStatBar];

// Функция для загрузки шрифта и конвертации в base64
const loadFontAsBase64 = async (fontUrl) => {
  try {
    const response = await fetch(fontUrl);
    const arrayBuffer = await response.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    let binaryString = "";
    for (let i = 0; i < uint8Array.byteLength; i++) {
      binaryString += String.fromCharCode(uint8Array[i]);
    }
    return btoa(binaryString);
  } catch (error) {
    console.error("Ошибка загрузки шрифта PDF файла:", error);
    return null;
  }
};

// Функция загрузки всех чартов в PDF
const downloadChart = async () => {
  // Экземпляр jsPDF
  const doc = new jsPDF({
    putOnlyUsedFonts: true,
    orientation: "portrait",
    unit: "mm",
    format: "a4"
  });

  // Дата формирования отчета
  const today = new Date().toLocaleDateString("ru-RU").replaceAll(".", "-");

  // Функция добавления фонового цвета на активную страницу
  const addBackgroundColor = () => {
    doc.setFillColor(238, 241, 244); // #EEF1F4
    doc.rect(0, 0, 210, 297, "F"); // A4 размер в мм (210x297)
  };

  // Добавляем фон для первой страницы
  addBackgroundColor();

  // Добавляем шрифты с поддержкой кириллицы (Roboto)
  try {
    const robotoRegularBase64 = await loadFontAsBase64(RobotoRegular);
    const robotoBoldBase64 = await loadFontAsBase64(RobotoBold);

    if (robotoRegularBase64) {
      doc.addFileToVFS("Roboto-Regular.ttf", robotoRegularBase64);
      doc.addFont("Roboto-Regular.ttf", "Roboto", "normal");
    }

    if (robotoBoldBase64) {
      doc.addFileToVFS("Roboto-Bold.ttf", robotoBoldBase64);
      doc.addFont("Roboto-Bold.ttf", "Roboto", "bold");
    }

    doc.setFont("Roboto");
  } catch (error) {
    console.error("Ошибка загрузки шрифтов:", error);
    doc.setFont("helvetica");
  }

  // Заголовок страницы
  doc.setFontSize(16);
  doc.text(`Общая аналитика проекта «${LKStore.project.name}»`, 5, 10);

  // Начальная позиция по Y после заголовка
  let yPosition = 20;

  try {
    const canvases = [];

    // Создания canvas'a с графиками.
    for (const element of chartsArray) {
      try {
        const elementNode = element.value;

        const canvas = await html2canvas(elementNode, {
          scale: 2,
          useCORS: true,
          logging: false,
          ignoreElements: (element) => {
            return element.id === "chart-control";
          },
        });

        canvases.push(canvas);
      } catch (error) {
        console.error("Ошибка при сохранение графика в canvas:", error);
      }
    }

    canvases.forEach((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const imgWidth = 200; // Ширина изображения в PDF
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Проверяем, помещается ли изображение на текущей странице
      if (yPosition + imgHeight > 280) {
        doc.addPage();
        addBackgroundColor(); // Добавляем фон для новой страницы
        yPosition = 10; // Сброс позиции для новой страницы
      }

      // Добавляем изображение в PDF
      doc.addImage(imgData, "PNG", 5, yPosition, imgWidth, imgHeight);

      // Обновляем позицию для следующего изображения
      yPosition += imgHeight + 5; // 5 - отступ между графиками
    });

    // Сохраняем PDF
    doc.save(`analytics-general_(${today}).pdf`);
  } catch (error) {
    console.error("Ошибка при создании PDF:", error);
  }
};

// Для доступа из родительского компонента AnalyticsPage
defineExpose({ downloadChart });
</script>

<style lang="scss" scoped>
.country-analytics-main {
  padding: 14px 0;
}
</style>
